import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { openStripeCheckout } from "@/lib/openStripeCheckout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { CalendarClock, MapPin, Ticket, Users, ExternalLink } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import ReportButton from "@/components/ReportButton";
import { thumb } from "@/lib/img";

const KIND_LABEL = { music: "Music", quiz: "Pub quiz", art: "Art", performance: "Performance", comedy: "Comedy", other: "Other" };

export default function GigLightbox({ gig, open, onOpenChange }) {
    const { user } = useAuth();
    const [rsvp, setRsvp] = useState(false);
    const [count, setCount] = useState(gig?.rsvp_count || 0);
    const [busy, setBusy] = useState(false);
    // Show a "Get a ticket" nudge right after the user says they're going. Auto-dismisses on close.
    const [showTicketNudge, setShowTicketNudge] = useState(false);
    const [ticketBusy, setTicketBusy] = useState(false);

    useEffect(() => {
        if (!gig) return;
        setCount(gig.rsvp_count || 0);
        setShowTicketNudge(false);
        if (user) {
            api.get(`/gigs/${gig.gig_id}/rsvp/status`).then(({ data }) => setRsvp(data.rsvp)).catch(() => {});
            api.get(`/gigs/${gig.gig_id}`).then(({ data }) => setCount(data.rsvp_count || 0)).catch(() => {});
        }
    }, [gig, user]);

    const hasExternalTickets = !!gig?.ticket_url;
    const hasPaidEntry = (gig?.price ?? 0) > 0;
    const ticketAvailable = !gig?.cancelled && (hasExternalTickets || hasPaidEntry);

    const toggle = async () => {
        if (!user) { toast("Sign in to RSVP"); return; }
        setBusy(true);
        try {
            const url = `/gigs/${gig.gig_id}/rsvp`;
            const { data } = rsvp ? await api.delete(url) : await api.post(url);
            setRsvp(data.rsvp);
            setCount(data.rsvp_count);
            if (data.rsvp) {
                toast.success("You're going!");
                if (ticketAvailable) setShowTicketNudge(true);
            } else {
                toast.success("RSVP removed");
                setShowTicketNudge(false);
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const buyTicket = async () => {
        if (hasExternalTickets) {
            window.open(gig.ticket_url, "_blank", "noreferrer");
            return;
        }
        if (!user) { toast("Sign in to buy a ticket"); return; }
        setTicketBusy(true);
        try {
            const { data } = await api.post(`/gigs/${gig.gig_id}/ticket-checkout`, {
                gig_id: gig.gig_id, quantity: 1, origin_url: window.location.origin,
            });
            if (data?.checkout_url) openStripeCheckout(data.checkout_url);
            else toast.error("Couldn't start checkout");
        } catch (e) { toast.error(apiError(e)); }
        finally { setTicketBusy(false); }
    };

    if (!gig) return null;
    const start = gig.start_at ? parseISO(gig.start_at) : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
                <VisuallyHidden.Root asChild><DialogTitle>{gig.title}</DialogTitle></VisuallyHidden.Root>
                <div className="relative aspect-[16/8] bg-stone-100">
                    {gig.cover_image && <img loading="lazy" decoding="async" src={thumb(gig.cover_image, 600)} alt={gig.title} className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute top-4 left-4 flex flex-col items-start gap-2">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white/90 backdrop-blur">
                            <span className="text-accent">●</span> {KIND_LABEL[gig.kind]}
                        </div>
                        {gig.cancelled && (
                            <div data-testid="gig-cancelled-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-destructive text-white uppercase tracking-wider shadow-lg">
                                Cancelled
                            </div>
                        )}
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                        <h2 data-testid="gig-title" className="font-display text-3xl md:text-4xl font-black tracking-tighter">{gig.title}</h2>
                        {start && (
                            <div className="mt-1 flex items-center gap-1 text-sm">
                                <CalendarClock size={14} /> {format(start, "EEE d MMM · HH:mm")}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <Link to={`/photographer/${gig.performer_id}`} onClick={() => onOpenChange(false)}
                            className="inline-flex items-center gap-3 group">
                            {gig.performer_picture ? (
                                <img loading="lazy" decoding="async" src={thumb(gig.performer_picture, 200)} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-xs font-bold">
                                    {gig.performer_name?.slice(0,2).toUpperCase()}
                                </div>
                            )}
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Performer</div>
                                <div className="font-display font-bold group-hover:underline">{gig.performer_name}</div>
                            </div>
                        </Link>
                        <div className="flex items-center gap-3">
                            <div className="text-sm text-muted-foreground inline-flex items-center gap-1">
                                <Users size={14} /> <span data-testid="gig-rsvp-count">{count}</span> going
                            </div>
                            <Button
                                data-testid="gig-rsvp-btn"
                                onClick={toggle}
                                disabled={busy || gig.cancelled}
                                variant={rsvp ? "outline" : "default"}
                                className={`rounded-full ${
                                    gig.cancelled
                                        ? "bg-stone-200 text-stone-500 hover:bg-stone-200 disabled:opacity-100"
                                        : rsvp
                                            ? "border-primary text-primary"
                                            : "bg-primary hover:bg-primary/90 hover:text-white"
                                }`}
                            >
                                <Ticket size={14} className="mr-1.5" />
                                {gig.cancelled ? "Cancelled" : rsvp ? "Going" : "I'm going"}
                            </Button>
                        </div>
                    </div>

                    {/* Ticket-purchase nudge that appears the moment a user RSVPs "going". */}
                    {showTicketNudge && rsvp && ticketAvailable && (
                        <div
                            data-testid="ticket-nudge"
                            className="mt-5 rounded-2xl border border-accent/40 bg-accent/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                        >
                            <div className="text-sm">
                                <div className="font-display font-bold flex items-center gap-1.5">
                                    <Ticket size={14} className="text-accent" /> Grab your ticket now?
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {hasExternalTickets
                                        ? "This gig sells tickets on an external site."
                                        : `Secure entry for £${(gig.price || 0).toFixed(2)} — pay with card.`}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    data-testid="ticket-nudge-dismiss"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowTicketNudge(false)}
                                    className="rounded-full text-muted-foreground"
                                >
                                    Not now
                                </Button>
                                <Button
                                    data-testid="ticket-nudge-buy"
                                    onClick={buyTicket}
                                    disabled={ticketBusy}
                                    className="rounded-full bg-accent hover:bg-accent/90 hover:text-white"
                                >
                                    {ticketBusy ? "Opening…" : hasExternalTickets ? (
                                        <>Buy ticket <ExternalLink size={12} className="ml-1.5" /></>
                                    ) : "Buy ticket"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {gig.description && <p className="mt-6 text-foreground/80 leading-relaxed">{gig.description}</p>}

                    <div className="mt-6 grid gap-3 text-sm">
                        {gig.address && (
                            <div className="flex items-start gap-2 text-muted-foreground">
                                <MapPin size={14} className="mt-0.5" />
                                {gig.venue_listing_id ? (
                                    <Link to={`/listing/${gig.venue_listing_id}`} onClick={() => onOpenChange(false)} className="hover:underline">{gig.address}</Link>
                                ) : gig.address}
                            </div>
                        )}
                        {gig.price != null && gig.price > 0 && (
                            <div className="text-sm">Entry <span className="font-display font-black text-lg">£{gig.price.toFixed(2)}</span></div>
                        )}
                        {/* Always-visible ticket action so users can still buy without RSVPing */}
                        {ticketAvailable && (
                            hasExternalTickets ? (
                                <a href={gig.ticket_url} target="_blank" rel="noreferrer">
                                    <Button data-testid="ticket-external-btn" variant="outline" size="sm" className="rounded-full">
                                        Tickets <ExternalLink size={12} className="ml-1.5" />
                                    </Button>
                                </a>
                            ) : (
                                <Button
                                    data-testid="ticket-buy-btn"
                                    onClick={buyTicket}
                                    disabled={ticketBusy}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full w-fit"
                                >
                                    <Ticket size={12} className="mr-1.5" />
                                    {ticketBusy ? "Opening…" : `Buy ticket · £${(gig.price || 0).toFixed(2)}`}
                                </Button>
                            )
                        )}
                    </div>
                    <div className="mt-6 flex justify-end">
                        <ReportButton
                            targetType="gig"
                            targetId={gig.gig_id}
                            targetLabel={gig.title}
                            hideForOwner={user?.user_id === gig.performer_id}
                            variant="compact"
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
